import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Alert, Backdrop, Box, Button, CircularProgress, Tooltip} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  TimeOfDayCountRow,
  TimeOfDayQuery,
  TimeOfDayResponse,
} from '../../../interfaces/HazardAITimeOfDay';
import {saveFile} from '../../../utils/file';
import SpeedSliderSelector from '../../common/SpeedSliderSelector';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {SitesSelect} from '../../selectors/SitesSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {HeatmapObjectSelector} from '../HazardHeatmap/HeatmapObjectSelector';
import {HeatmapZoneSelector} from '../HazardHeatmap/HeatmapZoneSelector';
import {TimeOfDayDetectionsModal} from './TimeOfDayDetectionsModal';
import {TimeOfDayGrid} from './TimeOfDayGrid';

const MAX_DAYS = 31;

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const TimeOfDayDetections = (props?: Props) => {
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [fetchedRows, setFetchedRows] = useState<TimeOfDayCountRow[]>([]);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selected, setSelected] = useState<{date: string; hour: number} | null>(
    null
  );

  const params = useMemo<TimeOfDayQuery>(
    () => ({
      date_start:
        filter.params?.date_start ??
        props?.value?.params?.date_start ??
        dayjs().subtract(MAX_DAYS - 1, 'day').format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        props?.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      site_id: _.has(filter.params, 'site_ids')
        ? filter.params?.site_ids
        : props?.value?.params?.site_id,
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : props?.value?.params?.safeye_node_id,
      object: filter.params?.object ?? undefined,
      zone: filter.params?.zone ?? undefined,
      speed_min: filter.params?.speed_min ?? 0,
      speed_max: filter.params?.speed_max ?? 200,
    }),
    [filter]
  );

  const fetchData = useCallback(async (p: TimeOfDayQuery) => {
    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<TimeOfDayResponse>(
        `${apiBaseUrl}/hazard-ai/detection-time-of-day`,
        {params: p}
      );
      setFetchedRows(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_tod01');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  }, []);

  useEffect(() => {
    fetchData(params);
    props?.onUpdate?.({...props.value, params});
  }, [params]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const resp = await API.get(
        `${apiBaseUrl}/hazard-ai/detection-time-of-day`,
        {params: {...params, export: 'excel'}, responseType: 'blob'}
      );
      saveFile(
        resp.data,
        `hazardai_time_of_day_${params.date_start}_${params.date_end}.xlsx`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_tod_xls');
      setFetchedErrors(messages);
    }
    setIsExporting(false);
  };

  const handleDateRangeChange = (v: [Date | null, Date | null]) => {
    setFilter(
      update(filter, {
        params: {
          date_start: {
            $set: v?.[0] ? dayjs(v[0]).format('YYYY-MM-DD') : undefined,
          },
          date_end: {
            $set: v?.[1] ? dayjs(v[1]).format('YYYY-MM-DD') : undefined,
          },
        },
      })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box display="flex" gap={1} py={1} flexWrap="wrap">
        <Box maxWidth={300} minWidth={160}>
          <SitesSelect
            size="small"
            multiple
            fullWidth
            value={params.site_id}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    site_ids: {$set: v as number[]},
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={300} minWidth={180}>
          <SafeyeNodeSelector
            size="small"
            fullWidth
            nullLabel="All Machines"
            value={
              params.safeye_node_id?.length
                ? +params.safeye_node_id[0]
                : null
            }
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    safeye_node_ids: {
                      $set: v != null ? [String(v)] : [],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400}>
          <DateRangeSelect
            value={[
              dayjs(params.date_start).toDate(),
              dayjs(params.date_end).toDate(),
            ]}
            size="small"
            maxDays={MAX_DAYS}
            onChange={handleDateRangeChange}
          />
        </Box>
        <Box maxWidth={300} minWidth={160}>
          <HeatmapObjectSelector
            size="small"
            fullWidth
            value={params.object}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    object: {$set: v ?? undefined},
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={300} minWidth={160}>
          <HeatmapZoneSelector
            size="small"
            fullWidth
            value={params.zone}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    zone: {$set: v ?? undefined},
                  },
                })
              );
            }}
          />
        </Box>
        <Box>
          <SpeedSliderSelector
            min={0}
            max={200}
            lowerValue={Number(params.speed_min)}
            upperValue={Number(params.speed_max)}
            onLowerChange={(v) => {
              setFilter(
                update(filter, {
                  params: {speed_min: {$set: v as number}},
                })
              );
            }}
            onUpperChange={(v) => {
              setFilter(
                update(filter, {
                  params: {speed_max: {$set: v as number}},
                })
              );
            }}
          />
        </Box>
        <Box ml="auto" display="flex" alignItems="center">
          <Tooltip title="Export to Excel">
            <Button
              size="small"
              variant="outlined"
              disabled={fetchedInProgress || isExporting || fetchedRows.length === 0}
              onClick={handleExport}
            >
              <FileDownloadIcon />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      {/* Heatmap grid */}
      <Box flex={1} overflow="hidden">
        <TimeOfDayGrid
          rows={fetchedRows}
          dateStart={params.date_start ?? ''}
          dateEnd={params.date_end ?? ''}
          onCellClick={(date, hour) => setSelected({date, hour})}
        />
      </Box>

      {selected != null && (
        <TimeOfDayDetectionsModal
          open
          date={selected.date}
          hour={selected.hour}
          filterParams={params}
          onClose={() => setSelected(null)}
        />
      )}
    </Box>
  );
};
